import { useEffect, useState } from "react";
import type { BridgeBillingNotice } from "@tuxx/orbit-bridge";
import { useOrbit } from "../provider";

interface OrbitBillingPanelProps {
  notices?: BridgeBillingNotice[];
}

export function OrbitBillingPanel({ notices: providedNotices }: OrbitBillingPanelProps) {
  const orbit = useOrbit();
  const [notices, setNotices] = useState<BridgeBillingNotice[]>([]);
  const [loading, setLoading] = useState(providedNotices === undefined);
  const [error, setError] = useState<string | null>(null);
  const visibleNotices = providedNotices ?? notices;

  useEffect(() => {
    if (providedNotices !== undefined) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await orbit.billing.list();
        if (!cancelled) setNotices(response.notices ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load billing notices");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orbit, providedNotices]);

  if (loading) return <div className="orbit-billing orbit-billing--loading">Loading billing updates...</div>;

  if (error) {
    return (
      <div className="orbit-billing orbit-billing--error">
        <strong>Billing updates could not load.</strong>
        <span>{error}</span>
      </div>
    );
  }

  if (visibleNotices.length === 0) {
    return (
      <div className="orbit-billing orbit-billing--empty">
        No billing actions right now. If anything needs attention, it will appear here and you will also receive an email prompt.
      </div>
    );
  }

  return (
    <div className="orbit-billing">
      {visibleNotices.map((notice) => (
        <article key={notice.id} className={`orbit-billing__notice orbit-billing__notice--${notice.status}`}>
          <div className="orbit-billing__head">
            <span className="orbit-billing__status">{formatStatus(notice.status)}</span>
            {notice.amount !== null ? (
              <span className="orbit-billing__amount">{formatMoney(notice.amount, notice.currency)}</span>
            ) : null}
          </div>
          <h4 className="orbit-billing__title">{notice.title}</h4>
          {notice.body ? <p className="orbit-billing__body">{notice.body}</p> : null}
          {notice.due_date ? <div className="orbit-billing__due">Due {formatDate(notice.due_date)}</div> : null}
          {notice.action_url ? (
            <a className="orbit-button orbit-billing__action" href={notice.action_url} target="_blank" rel="noreferrer">
              {notice.status === "overdue" || notice.status === "open" ? "Pay or review" : "Open billing"}
            </a>
          ) : null}
        </article>
      ))}
    </div>
  );
}

function formatStatus(status: BridgeBillingNotice["status"]): string {
  if (status === "open") return "Needs attention";
  return status.replace(/_/g, " ");
}

function formatMoney(amount: number, currency: string | null): string {
  const code = (currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount / 100);
  } catch {
    return `${code} ${(amount / 100).toFixed(2)}`;
  }
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}
